import { useEffect, useState } from 'react';
import { failureOf, said } from '@/api/errors';
import { useAuthority, useClaim, useDecideDraft, useRedraft, useUnclaim } from '@/api/hooks';
import type { DecideResult, WireDraft, WireMessage, WireSendAuthority, WireThread } from '@/api/wire';
import { Badge, Button, Kicker } from '@/ui/primitives';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { ComposedLine } from '@/ui/Composed';
import { actorLabel } from '@/today/copy';
import { PACE_WORDS, withoutReferences } from '@/outreach/words';
import { scanWords, useTechnical } from '@/app/technical';
import { Attachments } from '@/mail/Attachments';
import { explainDraft } from '@/mail/explain';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';

/**
 * A draft and who decides it (FRONTEND-AUDIT §D): the composer a person
 * sends or discards from, wherever the draft is shown — a thread, a Today
 * row, a record — and the claim on a conversation that takes it off
 * everyone else's day. Promoted from `mail/ThreadPage.tsx`, where they were
 * first needed, so no second screen draws its own.
 */

export function DraftComposer({
  draft,
  channel,
  source = [],
}: {
  draft: WireDraft;
  channel: WireThread['primaryChannel'];
  /** The thread's messages, so the scanner's findings on what the draft answers are shown beside it (R-002). */
  source?: WireMessage[];
}) {
  const [body, setBody] = useState(draft.body ?? '');
  const [again, setAgain] = useState(false);
  const decide = useDecideDraft();
  const authority = useAuthority(draft.id, channel);
  const toast = useToast();
  const who = useWho();
  useEffect(() => setBody(draft.body ?? ''), [draft.id, draft.body]);
  const edited = body.trim() !== (draft.body ?? '').trim();
  // Asked of the draft before anyone presses anything, and known again the
  // moment a send is refused for it: either way Send is off, and Discard
  // is the one way on (ADR-0081).
  const refusedFor = failureOf(decide.error);
  const suppressed =
    authority.data?.suppressed === true ||
    (decide.isError && refusedFor.kind === 'refused' && refusedFor.code === 'recipient_suppressed');

  const send = () =>
    decide.mutate(
      { draftId: draft.id, outcome: edited ? 'sent_edited' : 'sent_unchanged', ...(edited ? { body } : {}) },
      {
        onSuccess: (r) => toast({ text: sentWords(r, edited), tone: r.send && !r.send.dispatched ? 'danger' : undefined }),
        onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
      },
    );
  const discard = () =>
    decide.mutate(
      { draftId: draft.id, outcome: 'discarded' },
      {
        onSuccess: () =>
          toast({ text: `Discarded. ${actorLabel(draft.draftedByHandle ?? 'the agent')} will not send it — nothing does without your decision.` }),
        onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
      },
    );

  return (
    <section className="flex flex-col gap-3 rounded-brand border border-warning p-4" aria-label="Draft" data-testid="draft">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Kicker tone="accent">{draft.draftedByLabel ?? actorLabel(draft.draftedByHandle ?? draft.draftedBy)} drafted this</Kicker>
        <span className="text-dense text-meta">Also in your Gmail Drafts · edit here or there</span>
      </div>
      <ComposedLine composed={draft.composed} />
      <div className="text-dense text-secondary">To {draft.to.join(', ') || '—'}{draft.cc.length ? ` · cc ${draft.cc.join(', ')}` : ''}</div>
      <SourceFindings messages={source} />
      <Explainer
        label="What is this draft, and what happens next"
        context={explainDraft(draft, who, {
          // A draft is always drawn beside the conversation it answers, so it is not linked again.
          onThread: true,
          suppressed,
          edited,
          flagged: source.filter((m) => m.direction === 'inbound' && m.scanVerdict !== 'clean' && (m.scanFindings?.length ?? 0) > 0).length,
        })}
      />
      {suppressed && (
        <p className="m-0 border-l-2 border-danger pl-3 text-body" role="alert" data-testid="suppressed">
          Somebody this is addressed to asked us not to write again. It cannot be sent — by an agent or by you. Discard it, or
          start a new one to the others.
        </p>
      )}
      <textarea
        aria-label="Draft body"
        className="min-h-[180px] w-full rounded-brand border border-control bg-raised p-3 text-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <Attachments draftId={draft.id} />
      <div className="flex flex-wrap items-center gap-2">
        <Button icon="forward" variant="primary" onClick={send} disabled={decide.isPending || body.trim() === '' || suppressed}>Send as me</Button>
        <Button onClick={discard} disabled={decide.isPending}>Discard</Button>
        <Button variant="quiet" onClick={() => setAgain(true)} disabled={decide.isPending}>Draft again</Button>
        <span className="text-dense text-secondary" data-testid="authority">
          {edited ? 'Your edit goes out and the change is measured.' : 'Goes as written.'}{' '}
          {suppressed ? null : authorityWords(authority.data)}
        </span>
      </div>
      {decide.isError && <Refusal failure={refusedFor} compact />}
      {again && <DraftAgain draftId={draft.id} onClose={() => setAgain(false)} />}
    </section>
  );
}

/**
 * Written again, the way the person says (0129). What they say is kept as
 * how they write, so the next draft is written that way too; ticked, it is
 * proposed for the whole department, and its lead adopts it.
 */
function DraftAgain({ draftId, onClose }: { draftId: string; onClose: () => void }) {
  const redraft = useRedraft();
  const toast = useToast();
  const [instruction, setInstruction] = useState('');
  const [forDepartment, setForDepartment] = useState(false);
  return (
    <div className="flex flex-col gap-2 border-t border-line pt-3" role="group" aria-label="Draft again" data-testid="draft-again">
      <label className="flex flex-col gap-1 text-dense">
        <span className="font-semibold">How should it be written?</span>
        <textarea
          aria-label="How should it be written"
          className="min-h-[72px] w-full rounded-brand border border-control bg-raised p-2 text-body"
          placeholder="Shorter, warmer, no price in the first email, sign off with my first name…"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
        />
      </label>
      <label className="flex min-h-tap items-center gap-2 text-dense">
        <input type="checkbox" checked={forDepartment} onChange={(e) => setForDepartment(e.target.checked)} />
        Suggest it for everyone in the department (the lead decides)
      </label>
      <p className="m-0 text-dense text-secondary">This draft is discarded and written again. What you write here is kept, so the next drafts for you are written this way too; you can change it in Settings.</p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          disabled={!instruction.trim() || redraft.isPending}
          onClick={() =>
            redraft.mutate(
              { draftId, instruction: instruction.trim(), ...(forDepartment ? { forDepartment: true } : {}) },
              {
                onSuccess: () => {
                  toast({ text: 'Writing it again your way. It appears here when it is ready.' });
                  onClose();
                },
                onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
              },
            )
          }
        >
          Write it again
        </Button>
        <Button icon="close" variant="quiet" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}

/**
 * Why this goes as the person pressing Send, in the platform's own sentence.
 * The sentence is kept; only its references and the level's spelling are
 * put into words a person reads.
 */
function authorityWords(a: WireSendAuthority | undefined): string {
  if (!a) return 'It goes as you.';
  const said = withoutReferences(a.reason).replace(/\b(open|closed)_loop\b/g, '$1 loop');
  const why = said.charAt(0).toUpperCase() + said.slice(1);
  const pace = a.pace ? ` ${PACE_WORDS[a.pace.code].title}.` : '';
  // From a shared box, "on its own" means under the agent's disclosed name
  // and only under an approved campaign (ADR-0077, ADR-0086).
  if (a.unattended && a.sendAs === 'agent_disclosed') {
    return `It could leave on its own from the shared box, under the agent’s own name, while a campaign covers it; pressing Send sends it now on your say-so. ${why}${pace}`;
  }
  if (a.unattended) return `It could go on its own; pressing Send sends it as you now. ${why}.${pace}`;
  return `It goes as you. ${why}${why.endsWith('.') ? '' : '.'}${pace}`;
}

/** What happened, and only what happened: a send that did not leave says so. */
function sentWords(r: DecideResult, edited: boolean): string {
  if (!r.send) return 'Recorded.';
  if (!r.send.dispatched) {
    return 'Not sent. It is yours and queued, but nothing on this deployment can send mail yet — it leaves when that is set up.';
  }
  return edited ? 'Sent as you, with your edit. The change was measured.' : 'Sent as you, as written.';
}

export function ClaimControl({ threadId, claimedByHuman, me }: { threadId: string; claimedByHuman: string | null; me: string }) {
  const claim = useClaim();
  const unclaim = useUnclaim();
  const toast = useToast();
  if (claimedByHuman === me) {
    return (
      <Button size="row" onClick={() => unclaim.mutate(threadId, { onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })} disabled={unclaim.isPending}>
        You have it · hand it back
      </Button>
    );
  }
  if (claimedByHuman) {
    return <Badge tone="accent">Somebody is on it</Badge>;
  }
  return (
    <Button
      size="row"
      variant="primary"
      disabled={claim.isPending}
      onClick={() =>
        claim.mutate(threadId, {
          onSuccess: (r) =>
            toast({
              text: r.claimed
                ? "Claimed. It is off everyone else's day."
                : 'Somebody else is on it — it left your day the moment they claimed it.',
            }),
          onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
        })
      }
    >
      Claim it
    </Button>
  );
}

/**
 * What the scanner found in the messages a draft was written from (R-002).
 *
 * A flagged message still reaches the model that drafts from it — flagging
 * is not blocking — so the person deciding the draft is the last reader who
 * can notice that the reply carries out what the message asked. Shown as
 * the rule and its reason only: the excerpt would repeat the text itself.
 */
function SourceFindings({ messages }: { messages: WireMessage[] }) {
  const tech = useTechnical();
  const flagged = messages.filter(
    (m) => m.direction === 'inbound' && m.scanVerdict !== 'clean' && (m.scanFindings?.length ?? 0) > 0,
  );
  if (flagged.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 border-l-2 border-warning pl-3" role="note" data-testid="source-findings">
      <p className="m-0 text-body">
        Written from {flagged.length === 1 ? 'a message' : `${flagged.length} messages`} the scanner flagged. Read the
        reply for anything it was asked to do.
      </p>
      <ul className="m-0 flex list-none flex-col gap-1 p-0 text-dense text-secondary">
        {flagged.flatMap((m) =>
          (m.scanFindings ?? []).map((f, i) => (
            <li key={`${m.id}:${i}`} data-testid="source-finding">
              {tech && (
                <>
                  <span className="font-semibold">{f.pattern}</span> ({f.severity}) —{' '}
                </>
              )}
              {f.reason || 'no reason recorded'} · from {m.fromAddress}
            </li>
          )),
        )}
      </ul>
    </div>
  );
}

/** What the scanner said of a message or a turn, when it said more than clean (§11). */
export function ScanVerdict({ verdict }: { verdict: 'clean' | 'flagged' | 'blocked' | 'pending' | string }) {
  return (
    <span className="flex items-center gap-1.5 text-label text-secondary">
      <span aria-hidden className={`block h-2 w-2 ${verdict === 'blocked' ? 'bg-danger' : 'bg-warning'}`} />
      {scanWords(verdict)}
    </span>
  );
}
